import { DataFlowExpressionUtils } from '../expression.util';
import { BaseFormatAdapter } from './base-format.adapter';
import { DataFlowSource, IsolationLevel, SQLSourceOptions } from 'app/model/dataflow/dataflow-source';
import { DataFlowSink } from 'app/model/dataflow/dataflow-sink';
import { SqlBaseSettings } from 'app/model/dataflow/dataset-settings/sql-base-settings';
import { DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { ArrayDataFlowType, BooleanDataFlowType, StringDataFlowType } from 'app/model/dataflow/dataflow-datatype';
import { TableActionOption } from 'app/model/dataflow/dataset-settings/tabular-type-settings';
import { isTruthy } from 'app/model/dataflow/dataflow-expression-utilities';
import { HiveSettings } from 'app/model/dataflow/dataset-settings/hive-dataset-settings';
import { DataFlowSchemaAdapterUtils } from '../dataflow-schema-adapter-utils';
import { Metamodel, stringIsNullOrEmpty } from 'shell-models-lib';
import { DataFlowExpression } from 'app/model/dataflow/dataflowexpression';
import { LinkedServiceRegistryService } from 'app/registries/linked-service/linked-service-registry.service';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { DataFlowAdapterUtils } from '../adapter.util';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';

export class BaseSqlFormatAdapter extends BaseFormatAdapter<SqlBaseSettings> {
    private readonly _isTridentMdf: boolean = __IS_FABRIC_MDF__;
    protected async toSourceDSL(sqlBaseSettings: SqlBaseSettings): Promise<string[]> {
        const props = this._commonToDSL(sqlBaseSettings);

        if (sqlBaseSettings.query && sqlBaseSettings.format === SQLSourceOptions.Query) {
            props.push(`query: ${DataFlowExpressionUtils.escapeAndWrapConfigValue(sqlBaseSettings.query)}`);
        } else if (sqlBaseSettings.format === SQLSourceOptions.Procedure) {
            props.push(
                `procedureName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(sqlBaseSettings.storedProcName)}`
            );
            // MDF: Add database for SQL types, but skip for Warehouse (Warehouse artifact IS the database)
            if (this._isTridentMdf && sqlBaseSettings.store !== DatasetFormatType.Warehouse && !stringIsNullOrEmpty(sqlBaseSettings.databaseName.expression)) {
                props.push(
                    `database: ${DataFlowExpressionUtils.wrapExpressionConfigValue(sqlBaseSettings.databaseName)}`
                );
            }
            if (!stringIsNullOrEmpty(sqlBaseSettings.schemaName.expression)) {
                props.push(
                    `schemaName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(sqlBaseSettings.schemaName)}`
                );
            }
            props.push(
                `resultSet: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    sqlBaseSettings.storedProcResultSet,
                    BooleanDataFlowType.TYPE
                )}`
            );
        }

        if (
            sqlBaseSettings.transform &&
            sqlBaseSettings.transform instanceof DataFlowSource &&
            !(sqlBaseSettings instanceof HiveSettings)
        ) {
            sqlBaseSettings.isolationLevel.expression =
                sqlBaseSettings.isolationLevel.expression || IsolationLevel.ReadUncommitted;
            props.push(
                `isolationLevel: ${DataFlowExpressionUtils.wrapExpressionConfigValue(sqlBaseSettings.isolationLevel)}`
            );
        }

        if (sqlBaseSettings.format === SQLSourceOptions.Procedure && sqlBaseSettings.storedProcInputs.length) {
            const inputs = sqlBaseSettings.storedProcInputs
                .filter(
                    (input) =>
                        !stringIsNullOrEmpty(input.paramValue.expression) ||
                        !stringIsNullOrEmpty(input.paramName.expression)
                )
                // eslint-disable-next-line max-len
                .map(
                    (input) =>
                        `${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            input.paramName as DataFlowExpression,
                            StringDataFlowType.TYPE,
                            true
                        )} -> ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            input.paramValue as DataFlowExpression,
                            StringDataFlowType.TYPE,
                            true
                        )}`
                )
                .join(',');
            if (!!inputs) {
                props.push(`inputs:[${inputs}]`);
            }
        }

        const source = sqlBaseSettings.owner as DataFlowSource;
        if (source.partitionOption === 'Partitioning' && source.partitionMethod === 'external') {
            if (source.useQueryPartition && source.partitionPredicates) {
                const validExps = source.partitionPredicates.filter(
                    (exp) => exp.expression != null && exp.expression !== ''
                );
                const result = validExps
                    .map((s) => `'${DataFlowExpressionUtils.escapeSingleQuoteInDslString(s.expression)}'`)
                    .join(',');
                if (result) {
                    props.push(`partitionPredicates: [${result}]`);
                }
            } else {
                if (source.partitionColumn) {
                    if (source.partitionColumn.dynamicPartitionColumn?.templateExpression) {
                        props.push(
                            `partitionColumn: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                source.partitionColumn.dynamicPartitionColumn,
                                StringDataFlowType.TYPE
                            )}`
                        );
                    } else {
                        props.push(`partitionColumn: '${source.partitionColumn.serializableName}'`);
                    }
                }
            }
        }

        return props;
    }

    protected async toSinkDSL(sqlBaseSettings: SqlBaseSettings): Promise<string[]> {
        const props = this._commonToDSL(sqlBaseSettings);

        props.push(
            `insertable: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                sqlBaseSettings.allowInsert,
                BooleanDataFlowType.TYPE
            )}`
        );
        props.push(
            `updateable: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                sqlBaseSettings.allowUpdate,
                BooleanDataFlowType.TYPE
            )}`
        );
        props.push(
            `deletable: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                sqlBaseSettings.allowDelete,
                BooleanDataFlowType.TYPE
            )}`
        );
        props.push(
            `upsertable: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                sqlBaseSettings.allowUpsert,
                BooleanDataFlowType.TYPE
            )}`
        );

        if (sqlBaseSettings.transform instanceof DataFlowSink && sqlBaseSettings.transform.requiresKeyColumns) {
            const keyColumns = DataFlowSchemaAdapterUtils.getKeyColumnsProps(sqlBaseSettings);
            if (keyColumns) {
                props.push(keyColumns);
            }
        }

        if (sqlBaseSettings.tableActionOption === TableActionOption.Truncate) {
            props.push(`truncate: true`);
        }
        if (sqlBaseSettings.tableActionOption === TableActionOption.Recreate) {
            props.push(`recreate: true`);
        }

        if (isTruthy(sqlBaseSettings.skipKeyWrites)) {
            props.push(
                `skipKeyWrites:${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    sqlBaseSettings.skipKeyWrites,
                    BooleanDataFlowType.TYPE
                )}`
            );
        }

        if (sqlBaseSettings.isPostSqlsListOfExpression) {
            const postSQLScripts: string[] = [];
            sqlBaseSettings.postSQLScripts.forEach((script) => {
                if (script && script.expression) {
                    postSQLScripts.push(DataFlowExpressionUtils.escapeAndWrapConfigValue(script as DataFlowExpression));
                }
            });
            if (postSQLScripts.length) {
                props.push(`postSQLs:[${postSQLScripts.join(', ')}]`);
            }
        } else {
            if (
                sqlBaseSettings.postSQLScripts &&
                sqlBaseSettings.postSQLScripts[0] &&
                sqlBaseSettings.postSQLScripts[0].expression
            ) {
                props.push(
                    `postSQLs: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                        sqlBaseSettings.postSQLScripts[0],
                        ArrayDataFlowType.TYPE
                    )}`
                );
            }
        }

        const linkedService =
            sqlBaseSettings.transform instanceof DataFlowSink && sqlBaseSettings.transform.sinkLinkedService;
        const supportSpecifyingTmpDB =
            !!linkedService &&
            (
                await getServiceInstance<LinkedServiceRegistryService>(
                    this.context.contextData,
                    'LinkedServiceRegistryService'
                )
            ).getLinkedServiceConfiguration(Metamodel.modelOf(linkedService).name).dataflowSinkStagingSchema;
        const linkedServiceType = linkedService?.getLinkedServiceType();
        if (
            supportSpecifyingTmpDB &&
            !sqlBaseSettings.useTmpDB &&
            (linkedServiceType !== adfModelTypeMap.snowflake.linkedServiceType ||
                DataFlowAdapterUtils.isStagingSchemaPropertyValidForSnowflake(
                    linkedServiceType,
                    sqlBaseSettings?.stagingSchemaName?.expression
                ))
        ) {
            props.push(
                `stagingSchemaName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    sqlBaseSettings.stagingSchemaName
                )}`
            );
        }

        return props;
    }

    protected async toSourceModel(
        owner: DataFlowSource,
        configs: Map<string, string>,
        handledConfigs: string[],
        sqlBaseSettings?: SqlBaseSettings
    ): Promise<SqlBaseSettings> {
        this._commonToModel(owner, configs, handledConfigs, sqlBaseSettings);

        if (configs.has('query')) {
            sqlBaseSettings.query = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('query'),
                owner,
                StringDataFlowType.TYPE
            );
            if (!sqlBaseSettings.query.templateExpression) {
                DataFlowExpressionUtils.unescapeConfigValue(sqlBaseSettings.query);
            }
            handledConfigs.push('query');
        }

        if (configs.has('procedureName')) {
            sqlBaseSettings.storedProcName = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('procedureName'),
                owner,
                StringDataFlowType.TYPE
            );
            handledConfigs.push('procedureName');
        }

        if (configs.has('resultSet')) {
            sqlBaseSettings.storedProcResultSet = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('resultSet'),
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('resultSet');
        }

        if (configs.has('inputs')) {
            sqlBaseSettings.storedProcInputs = DataFlowExpressionUtils.parseExpressionMapInDsl(
                configs.get('inputs'),
                owner,
                StringDataFlowType.TYPE
            );
            handledConfigs.push('inputs');
        }

        return sqlBaseSettings;
    }

    protected async toSinkModel(
        owner: DataFlowSink,
        configs: Map<string, string>,
        handledConfigs: string[],
        sqlBaseSettings?: SqlBaseSettings
    ): Promise<SqlBaseSettings> {
        this._commonToModel(owner, configs, handledConfigs, sqlBaseSettings);

        if (configs.has('insertable')) {
            sqlBaseSettings.allowInsert = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('insertable'),
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('insertable');
        }
        if (configs.has('updateable')) {
            sqlBaseSettings.allowUpdate = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('updateable'),
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('updateable');
        }
        if (configs.has('deletable')) {
            sqlBaseSettings.allowDelete = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('deletable'),
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('deletable');
        }
        if (configs.has('upsertable')) {
            sqlBaseSettings.allowUpsert = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('upsertable'),
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('upsertable');
        }

        if (configs.has('keys')) {
            const expression = configs.get('keys');
            sqlBaseSettings.isListOfKeyColumns = expression.startsWith('[') && expression.endsWith(']');
            sqlBaseSettings.keyColumns = sqlBaseSettings.isListOfKeyColumns
                ? DataFlowExpressionUtils.parseExpressionArrayInDsl(expression, owner, StringDataFlowType.TYPE, true)
                : [DataFlowExpressionUtils.unwrapExpressionConfigValue(expression, owner, 'string[]', true)];
            handledConfigs.push('keys');
        }

        if (configs.has('truncate')) {
            sqlBaseSettings.tableActionOption =
                configs.get('truncate') === 'true' ? TableActionOption.Truncate : sqlBaseSettings.tableActionOption;
            handledConfigs.push('truncate');
        }
        if (configs.has('recreate')) {
            sqlBaseSettings.tableActionOption =
                configs.get('recreate') === 'true' ? TableActionOption.Recreate : sqlBaseSettings.tableActionOption;
            handledConfigs.push('recreate');
        }
        if (configs.has('skipKeyWrites')) {
            sqlBaseSettings.skipKeyWrites = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('skipKeyWrites'),
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('skipKeyWrites');
        }

        if (configs.has('postSQLs')) {
            const expression = configs.get('postSQLs');
            if (expression.startsWith('[') && expression.endsWith(']')) {
                sqlBaseSettings.isPostSqlsListOfExpression = true;
                const expressionArray = DataFlowExpressionUtils.parseExpressionArrayInDsl(expression, owner);
                if (expressionArray.length > 0) {
                    sqlBaseSettings.postSQLScripts = expressionArray;
                    sqlBaseSettings.postSQLScripts.forEach((script) => {
                        if (!script.templateExpression) {
                            DataFlowExpressionUtils.unescapeConfigValue(script as DataFlowExpression);
                        }
                    });
                }
            } else {
                sqlBaseSettings.isPostSqlsListOfExpression = false;
                sqlBaseSettings.postSQLScripts = [
                    DataFlowExpressionUtils.unwrapExpressionConfigValue(expression, owner, ArrayDataFlowType.TYPE)
                ];
            }
            handledConfigs.push('postSQLs');
        }

        if (configs.has('stagingSchemaName')) {
            sqlBaseSettings.stagingSchemaName = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('stagingSchemaName'),
                owner
            );
            sqlBaseSettings.useTmpDB = false;
            handledConfigs.push('stagingSchemaName');
        }
        return sqlBaseSettings;
    }

    private _commonToDSL(sqlBaseSettings: SqlBaseSettings): string[] {
        const props = [];
        props.push(`format: '${sqlBaseSettings.format}'`);
        props.push(`store: '${sqlBaseSettings.store}'`);
        if (sqlBaseSettings.format === 'table') {
            // MDF: Add database for SQL types, but skip for Warehouse (Warehouse artifact IS the database)
            if (this._isTridentMdf && sqlBaseSettings.store !== DatasetFormatType.Warehouse) {
                props.push(`database: ${DataFlowExpressionUtils.wrapExpressionConfigValue(sqlBaseSettings.databaseName)}`);
            }
            
            props.push(`schemaName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(sqlBaseSettings.schemaName)}`);
            props.push(`tableName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(sqlBaseSettings.tableName)}`);
        }
        if (sqlBaseSettings.isPreSqlsListOfExpression) {
            const preSQLScripts: string[] = [];
            sqlBaseSettings.preSQLScripts.forEach((script) => {
                if (script && script.expression) {
                    preSQLScripts.push(DataFlowExpressionUtils.escapeAndWrapConfigValue(script));
                }
            });
            if (preSQLScripts.length) {
                props.push(`preSQLs:[${preSQLScripts.join(', ')}]`);
            }
        } else {
            if (
                sqlBaseSettings.preSQLScripts &&
                sqlBaseSettings.preSQLScripts[0] &&
                sqlBaseSettings.preSQLScripts[0].expression
            ) {
                props.push(
                    `preSQLs: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                        sqlBaseSettings.preSQLScripts[0],
                        ArrayDataFlowType.TYPE
                    )}`
                );
            }
        }
        return props;
    }

    private _commonToModel(
        owner: DataFlowSource | DataFlowSink,
        configs: Map<string, string>,
        handledConfigs: string[],
        sqlBaseSettings?: SqlBaseSettings
    ): SqlBaseSettings {
        if (this._isTridentMdf && configs.has('database')) {
            sqlBaseSettings.databaseName = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('database'),
                owner
            );
            handledConfigs.push('database');
        }
        if (configs.has('schemaName')) {
            sqlBaseSettings.schemaName = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('schemaName'),
                owner
            );
            handledConfigs.push('schemaName');
        }
        if (configs.has('tableName')) {
            sqlBaseSettings.tableName = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('tableName'),
                owner
            );
            handledConfigs.push('tableName');
        }
        if (configs.has('format')) {
            sqlBaseSettings.format = DataFlowExpressionUtils.trimSpacesAndQuotesInDslString(configs.get('format'));
            handledConfigs.push('format');
        }
        if (configs.has('isolationLevel')) {
            sqlBaseSettings.isolationLevel = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('isolationLevel'),
                owner
            );
            handledConfigs.push('isolationLevel');
        }

        if (configs.has('preSQLs')) {
            const expression = configs.get('preSQLs');
            if (expression.startsWith('[') && expression.endsWith(']')) {
                sqlBaseSettings.isPreSqlsListOfExpression = true;
                const expressionArray = DataFlowExpressionUtils.parseExpressionArrayInDsl(expression, owner);
                if (expressionArray.length > 0) {
                    sqlBaseSettings.preSQLScripts = expressionArray;
                    sqlBaseSettings.preSQLScripts.forEach((script) => {
                        if (!script.templateExpression) {
                            DataFlowExpressionUtils.unescapeConfigValue(script as DataFlowExpression);
                        }
                    });
                }
            } else {
                sqlBaseSettings.isPreSqlsListOfExpression = false;
                sqlBaseSettings.preSQLScripts = [
                    DataFlowExpressionUtils.unwrapExpressionConfigValue(expression, owner, ArrayDataFlowType.TYPE)
                ];
            }
            handledConfigs.push('preSQLs');
        }

        return sqlBaseSettings;
    }
}
